import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { BannerMessage } from '@/common/BannerMessage';
import { LocalTabsBar } from '@/common/LocalTabsBar';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { SeriesSection } from '@/courses/SeriesSection';
import type { Series } from '@/courses/types';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { apiLanguage } from '~/i18n';
import { localeToApiLanguage } from '~/utils/languageFormat';

export const Route = createFileRoute('/_app/all-courses/')({
  component: CoursesScreen,
  beforeLoad: () => {
    return {
      pageBackground: 'white',
    };
  },
});

export default function CoursesScreen() {
  const { t } = useTranslation();
  const { me, isStudent } = useCurrentUser();
  const memberId = String(me?.id || '');

  // The interface language, not the account language: legacy renders the public
  // directory per interface language, and signed-out visitors reach it.
  // `language` is part of the query key, so a language switch refetches with no
  // extra invalidation.
  const language = localeToApiLanguage(apiLanguage());

  const { data: seriesData, isLoading: isLoadingSeries } =
    api.listSeries.useQuery({
      query: { language, limit: 50 },
    });
  const seriesList = seriesData?.data ?? [];

  const { data: selfPacedData, isLoading: isLoadingSelfPaced } =
    api.listSelfPacedCourses.useQuery({
      query: { language, limit: 50 },
    });
  const selfPacedCourses = selfPacedData?.data ?? [];

  // A synthetic group, not a series row: legacy's Self Paced set is the
  // published courses in no series, rendered as the last group, so it's
  // appended rather than sorted in. `-1` is the id the liteapp catalogue gives
  // this group, and no real series can collide with it.
  //
  // Rendered only when it has courses; some languages have none, so they get no
  // tab, as in legacy.
  const groups: Series[] = selfPacedCourses.length
    ? [
        ...seriesList,
        {
          id: -1,
          title: t('lessons.self-paced-course'),
          courses: selfPacedCourses,
        },
      ]
    : seriesList;

  const groupTabs = groups.map((s) => ({
    title: s.title,
    id: String(s.id),
  }));

  const [selectedId, setSelectedId] = React.useState<string | undefined>();

  // Derived rather than an effect: after a language switch the selection may
  // name a series the new payload doesn't have, and pinning it would leave a
  // tab strip with nothing under it. The payload arrives in legacy's order, so
  // the fallback is the first tab legacy shows.
  const selectedGroup =
    groups.find((s) => String(s.id) === selectedId) ?? groups[0];

  // Both reads gate the first paint, so the Self Paced tab is present in the
  // strip's first render rather than appended to a strip the viewer has already
  // read. On an error `isLoading` clears with no data, which renders the strip
  // without that tab — the same shape a language with no self-paced course gets.
  if (isLoadingSeries || isLoadingSelfPaced) {
    return (
      <PageContainer>
        <div style={{ margin: '9px 0 9px 0' }}>
          <TitleWithQuestion
            title={t('navigation.courses')}
            placement="courses"
          />
        </div>
        <LoadingText>{t('lessons.loading-all-courses')}</LoadingText>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div style={{ margin: '9px 0 9px 0' }}>
        <TitleWithQuestion
          title={t('navigation.courses')}
          placement="courses"
        />
      </div>
      {/* Students only, as in legacy. */}
      {isStudent ? (
        <div style={{ margin: '26px 0' }}>
          <BannerMessage
            text={t('lessons.courses-directory-notice')}
            buttonText={t('lessons.view-my-progress')}
            buttonTo="/members/$memberId/course-study"
            buttonParams={{ memberId }}
          />
        </div>
      ) : null}
      {selectedGroup && (
        <div style={{ margin: '18px 0' }}>
          <LocalTabsBar
            tabs={groupTabs}
            selectedId={String(selectedGroup.id)}
            onTabClick={(tab) => setSelectedId(tab.id)}
          />
        </div>
      )}

      {/* A series with no courses keeps its tab and renders an empty
         section, as in legacy, so this must not fall back to the loading
         text. */}
      {selectedGroup && <SeriesSection series={selectedGroup} />}
    </PageContainer>
  );
}

const PageContainer = styled.div`
  box-sizing: border-box;
  padding: 24px;
  width: 100%;
`;

const LoadingText = styled.div`
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 16px;
  padding: 24px 0;
`;
